import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import { confermaCancellaDelega } from '../lib/cancella-delega.ts';
import { enforcePerm } from '../lib/perm.ts';

type ModalInstance = { show(): void; hide(): void; dispose(): void };
type TabName = 'deleghe' | 'disdette' | 'storico';

export class DettaglioLavoratore extends AppComponent {
  static override componentName = 'wc-dettaglio-lavoratore';

  protected override get templateId() { return 'dettaglio-lavoratore'; }

  private lavoratoreId = 0;
  private loadedTabs = new Set<TabName>();
  private delegheLavoratore: Record<string, unknown>[] = [];
  private modals: ModalInstance[] = [];

  protected override afterRender(): void {
    this.lavoratoreId = this.attributeNumber('lavoratore-id');
    if (!this.lavoratoreId) {
      this.showError('ID lavoratore non specificato');
      return;
    }
    this.setupDelegheActions();
    this.loadDettaglio();
  }

  // --- Azioni tab deleghe (setup una volta in afterRender) ---

  private setupDelegheActions(): void {
    this.delegate.on('click', '[data-action="nuova-delega"]', async () => {
      const result = await Weext.modalIframe(`/deleghe/nuova?lavoratoreId=${this.lavoratoreId}&noAggiorna=1&layout=modal`, {
        title: 'Nuova delega',
        size: 'lg',
      });
      if (result.refresh) this.reloadDelegheTab();
    });

    this.delegate.on('click', '[data-action="dettagli-delega"]', async (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-action="dettagli-delega"]') as HTMLElement;
      const id = Number(el?.dataset.id);
      if (id) this.showDettaglioDelega(id);
    });

    this.delegate.on('click', '[data-action="modifica-delega"]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-action="modifica-delega"]') as HTMLElement;
      const id = Number(el?.dataset.id);
      if (id) this.handleModificaDelega(id);
    });

    this.delegate.on('click', '[data-action="cancella-delega"]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-action="cancella-delega"]') as HTMLElement;
      const id = Number(el?.dataset.id);
      if (id) this.handleCancellaDelega(id);
    });
  }

  // --- Dettaglio e tab ---

  private async loadDettaglio(): Promise<void> {
    const result = await this.api.lavoratori.dettaglio(this.lavoratoreId);
    const container = this.$('[data-content]');
    if (!container) return;

    if (!result.ok) {
      if (result.status === 404) {
        container.innerHTML =
          '<div class="text-center py-4">' +
          '<p class="text-muted mb-3">Lavoratore non trovato</p>' +
          '<a href="/lavoratori" class="btn btn-outline-secondary btn-sm">' +
          '<i class="bi bi-arrow-left me-1"></i>Torna alla ricerca</a></div>';
      } else {
        this.showError(result.error);
      }
      return;
    }

    const data = result.data as Record<string, unknown>;
    templateParseToElement('dettaglio-lavoratore-scheda', container, data);

    this.setupTabListeners();
    this.loadTab('deleghe');
  }

  private setupTabListeners(): void {
    const tabContainer = this.$('.nav-tabs');
    if (!tabContainer) return;

    this.listen(tabContainer, 'shown.bs.tab', (e: Event) => {
      const target = e.target as HTMLElement;
      const tabName = target.dataset.tab as TabName | undefined;
      if (tabName && !this.loadedTabs.has(tabName)) {
        this.loadTab(tabName);
      }
    });
  }

  private async loadTab(tabName: TabName): Promise<void> {
    const container = this.$(`[data-tab-content="${tabName}"]`);
    if (!container) return;

    templateParseToElement('shared-loading-state', container, {});

    const loaders: Record<TabName, () => Promise<void>> = {
      deleghe: () => this.loadDeleghe(container),
      disdette: () => this.loadDisdette(container),
      storico: () => this.loadStorico(container),
    };

    await loaders[tabName]();
    this.loadedTabs.add(tabName);
  }

  private async loadDeleghe(container: HTMLElement): Promise<void> {
    const result = await this.api.lavoratori.deleghe(this.lavoratoreId);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }
    const deleghe = result.data.deleghe;
    this.delegheLavoratore = deleghe.map(d => ({ ...d } as Record<string, unknown>));
    templateParseToElement('dettaglio-lavoratore-deleghe', container, { deleghe });
    enforcePerm(container);
    this.appendDelegheModals();
  }

  private async loadDisdette(container: HTMLElement): Promise<void> {
    const result = await this.api.lavoratori.disdette(this.lavoratoreId);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }
    templateParseToElement('dettaglio-lavoratore-disdette', container, { disdette: result.data.disdette });
  }

  private async loadStorico(container: HTMLElement): Promise<void> {
    const result = await this.api.lavoratori.storico(this.lavoratoreId);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }
    templateParseToElement('dettaglio-lavoratore-storico', container, { storico: result.data.storico });
  }

  // --- Modale Bootstrap ---

  private appendDelegheModals(): void {
    for (const modal of this.$$('[data-modal]')) {
      if (!modal.id) continue;
      const existing = document.getElementById(modal.id);
      if (existing && existing !== modal) existing.remove();
      document.body.appendChild(modal);
    }
  }

  private getModal(selector: string): ModalInstance | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const bs = (globalThis as any).bootstrap;
    const existing = bs?.Modal?.getInstance(el);
    if (existing) return existing;
    const m = new bs.Modal(el);
    this.modals.push(m);
    return m;
  }

  // --- Dettaglio delega (modale informativa) ---

  private async showDettaglioDelega(id: number): Promise<void> {
    const root = document.getElementById('modalDettaglioDelega');
    if (!root) return;
    const content = root.querySelector('[data-delega-content]') as HTMLElement;
    if (!content) return;

    const delega = this.delegheLavoratore.find(d => Number(d.id) === id);
    if (!delega) return;

    templateParseToElement('shared-dettaglio-delega', content, {
      ...delega,
      operatoreNome: delega.operatoreNome ?? null,
    });
    this.getModal('#modalDettaglioDelega')?.show();
  }

  // --- Azioni riga delega ---

  private async handleModificaDelega(id: number): Promise<void> {
    const result = await Weext.modalIframe(`/deleghe/${id}/modifica?layout=modal`, {
      title: 'Modifica delega',
      size: 'lg',
    });
    if (result.refresh) this.reloadDelegheTab();
  }

  private async handleCancellaDelega(id: number): Promise<void> {
    if (await confermaCancellaDelega(this.api, id)) this.reloadDelegheTab();
  }

  // --- Reload tab deleghe ---

  private reloadDelegheTab(): void {
    this.loadedTabs.delete('deleghe');
    this.loadTab('deleghe');
  }

  // --- Helpers ---

  private showError(message: string): void {
    const container = this.$('[data-content]');
    if (container) {
      templateParseToElement('shared-error-state', container, { message });
    }
  }

  // --- Cleanup ---

  protected override onDisconnected(): void {
    for (const m of this.modals) {
      try { m.dispose(); } catch { /* ignore */ }
    }
    this.modals = [];
    document.getElementById('modalDettaglioDelega')?.remove();
  }
}
